import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, adminApi, LOGO_URL } from "../lib/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, LogOut, ArrowLeft, Star, Package } from "lucide-react";
import OrdersPanel from "../components/OrdersPanel";

const emptyForm = {
  name: "",
  description: "",
  price: 0,
  image_url: "",
  category: "tees",
  sizes: ["S", "M", "L", "XL"],
  stock: 0,
  featured: false,
};

export default function Admin() {
  const navigate = useNavigate();
  const [token, setToken] = useState(
    localStorage.getItem("allstar_admin_token") || ""
  );
  const [authed, setAuthed] = useState(
    !!localStorage.getItem("allstar_admin_token")
  );
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [siteOpen, setSiteOpen] = useState(false);
  const [tab, setTab] = useState("products");

  const loadProducts = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products");
      setProducts(data);
    } catch (e) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const { data } = await api.get("/settings");
      setSiteOpen(!!data.site_open);
    } catch {}
  };

  const toggleSite = async () => {
    try {
      const { data } = await adminApi().put("/settings", {
        site_open: !siteOpen,
      });
      setSiteOpen(!!data.site_open);
      toast.success(
        data.site_open ? "Site ouvert au public 🟢" : "Site fermé (Coming Soon) 🔒"
      );
    } catch {
      toast.error("Erreur");
    }
  };

  useEffect(() => {
    if (authed) {
      loadProducts();
      loadSettings();
    }
  }, [authed]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/login", { token });
      localStorage.setItem("allstar_admin_token", token);
      setAuthed(true);
      toast.success("Welcome back, boss.");
    } catch {
      toast.error("Invalid token");
    }
  };

  const logout = () => {
    localStorage.removeItem("allstar_admin_token");
    setToken("");
    setAuthed(false);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      description: p.description || "",
      price: p.price,
      image_url: p.image_url,
      category: p.category,
      sizes: p.sizes || [],
      stock: p.stock || 0,
      featured: !!p.featured,
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      stock: parseInt(form.stock) || 0,
      sizes:
        typeof form.sizes === "string"
          ? form.sizes.split(",").map((s) => s.trim()).filter(Boolean)
          : form.sizes,
    };
    try {
      const client = adminApi();
      if (editing) {
        await client.put(`/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await client.post("/products", payload);
        toast.success("Product created");
      }
      setShowForm(false);
      setEditing(null);
      loadProducts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await adminApi().delete(`/products/${p.id}`);
      toast.success("Deleted");
      loadProducts();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  // ---------- LOGIN SCREEN ----------
  if (!authed) {
    return (
      <div
        className="min-h-screen bg-black text-white flex items-center justify-center px-6"
        data-testid="admin-login-screen"
      >
        <div className="w-full max-w-md border border-zinc-900 bg-zinc-950 p-10">
          <img
            src={LOGO_URL}
            alt="ALLSTAR"
            className="h-16 w-16 object-contain mb-6"
          />
          <p className="text-xs uppercase tracking-[0.3em] text-green-400 mb-2">
            Control Room
          </p>
          <h1 className="font-display text-4xl uppercase tracking-tighter mb-8">
            Admin Access
          </h1>
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500 mb-2 block">
                Admin Token
              </label>
              <input
                data-testid="admin-token-input"
                type="password"
                autoFocus
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="enter admin token"
                className="w-full bg-black border border-zinc-800 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none px-4 py-3 text-white placeholder-zinc-700"
              />
            </div>
            <button
              data-testid="admin-login-btn"
              type="submit"
              className="w-full bg-green-500 hover:bg-green-400 text-black font-bold uppercase tracking-widest py-4 transition-colors"
            >
              Enter
            </button>
          </form>
          <button
            onClick={() => navigate("/")}
            data-testid="admin-back-home"
            className="mt-6 text-xs uppercase tracking-widest text-zinc-500 hover:text-green-400 flex items-center gap-2"
          >
            <ArrowLeft size={14} /> Back to site
          </button>
        </div>
      </div>
    );
  }

  // ---------- DASHBOARD ----------
  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-dashboard">
      <header className="border-b border-zinc-900 bg-black/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="" className="h-10 w-10 object-contain" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-green-400">
                Control Room
              </p>
              <h1 className="font-display text-xl uppercase tracking-tighter">
                ALLSTAR Admin
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/")}
              data-testid="admin-view-site-btn"
              className="text-xs uppercase tracking-widest border border-zinc-800 hover:border-green-400 hover:text-green-400 px-4 py-2 transition-colors"
            >
              View site
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="text-xs uppercase tracking-widest border border-zinc-800 hover:border-red-500 hover:text-red-500 px-4 py-2 flex items-center gap-2 transition-colors"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 md:px-10 py-12">
        {/* Site status panel */}
        <div
          data-testid="site-status-panel"
          className="border border-zinc-900 bg-zinc-950 p-6 mb-10 flex flex-col md:flex-row md:items-center justify-between gap-4"
        >
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
              Site Status
            </p>
            <div className="flex items-center gap-3">
              <span
                className={`h-3 w-3 ${siteOpen ? "bg-green-400" : "bg-red-500"}`}
              />
              <h3 className="font-display text-2xl uppercase tracking-tighter">
                {siteOpen ? "OPEN — Public visible" : "CLOSED — Coming Soon"}
              </h3>
            </div>
            <p className="text-xs text-zinc-500 mt-2">
              {siteOpen
                ? "Visitors see the full shop."
                : "Visitors see the Coming Soon page only. Use ?preview=1 to bypass."}
            </p>
          </div>
          <button
            onClick={toggleSite}
            data-testid="toggle-site-btn"
            className={`uppercase tracking-widest text-xs font-bold px-6 py-4 transition-colors ${
              siteOpen
                ? "bg-red-500 hover:bg-red-400 text-black"
                : "bg-green-500 hover:bg-green-400 text-black"
            }`}
          >
            {siteOpen ? "Lock site (Coming Soon)" : "Open site to public"}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-zinc-900 mb-10">
          <button
            onClick={() => setTab("products")}
            data-testid="tab-products"
            className={`px-6 py-3 text-xs uppercase tracking-widest font-bold transition-colors border-b-2 -mb-px ${
              tab === "products"
                ? "border-green-400 text-white"
                : "border-transparent text-zinc-500 hover:text-white"
            }`}
          >
            Products
          </button>
          <button
            onClick={() => setTab("orders")}
            data-testid="tab-orders"
            className={`px-6 py-3 text-xs uppercase tracking-widest font-bold transition-colors border-b-2 -mb-px flex items-center gap-2 ${
              tab === "orders"
                ? "border-green-400 text-white"
                : "border-transparent text-zinc-500 hover:text-white"
            }`}
          >
            <Package size={14} /> Orders
          </button>
        </div>

        {tab === "orders" && <OrdersPanel />}
        {tab === "products" && (
          <>
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tighter">
                  Products
                </h2>
                <p className="text-zinc-500 text-sm mt-2 uppercase tracking-widest">
                  {products.length} {products.length === 1 ? "piece" : "pieces"} live
                </p>
              </div>
              <button
                onClick={openCreate}
                data-testid="admin-new-product-btn"
                className="bg-green-500 hover:bg-green-400 text-black font-bold uppercase tracking-widest px-6 py-3 flex items-center gap-2 transition-colors"
              >
                <Plus size={16} /> New product
              </button>
            </div>

            <div className="border border-zinc-900">
          <div className="grid grid-cols-12 text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-900 px-4 py-3">
            <div className="col-span-5">Product</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-2">Price</div>
            <div className="col-span-1">Stock</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>

          {loading && (
            <div className="p-10 text-center text-zinc-500">Loading…</div>
          )}

          {!loading &&
            products.map((p) => (
              <div
                key={p.id}
                data-testid={`admin-row-${p.id}`}
                className="grid grid-cols-12 items-center border-b border-zinc-900 px-4 py-4 hover:bg-zinc-950/60 transition-colors"
              >
                <div className="col-span-5 flex items-center gap-4">
                  <img
                    src={p.image_url}
                    alt=""
                    className="w-14 h-14 object-cover bg-zinc-900 shrink-0"
                  />
                  <div className="min-w-0">
                    <p className="font-display uppercase tracking-tight text-white flex items-center gap-2 truncate">
                      {p.name}
                      {p.featured && (
                        <Star
                          size={12}
                          className="text-green-400 shrink-0"
                          fill="currentColor"
                        />
                      )}
                    </p>
                    <p className="text-xs text-zinc-500 truncate">
                      {p.description || "—"}
                    </p>
                  </div>
                </div>
                <div className="col-span-2 text-xs uppercase tracking-widest text-zinc-400">
                  {p.category}
                </div>
                <div className="col-span-2 font-display text-green-400">
                  ${p.price.toFixed(2)}
                </div>
                <div className="col-span-1 text-zinc-300">{p.stock}</div>
                <div className="col-span-2 flex gap-2 justify-end">
                  <button
                    onClick={() => openEdit(p)}
                    data-testid={`admin-edit-${p.id}`}
                    className="border border-zinc-800 hover:border-green-400 hover:text-green-400 p-2 transition-colors"
                    aria-label="edit"
                  >
                    <Edit size={14} />
                  </button>
                  <button
                    onClick={() => remove(p)}
                    data-testid={`admin-delete-${p.id}`}
                    className="border border-zinc-800 hover:border-red-500 hover:text-red-500 p-2 transition-colors"
                    aria-label="delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}

          {!loading && products.length === 0 && (
            <div className="p-16 text-center text-zinc-500 uppercase tracking-widest text-sm">
              No products yet. Drop the first one.
            </div>
          )}
        </div>
          </>
        )}
      </main>

      {/* FORM MODAL */}
      {showForm && (
        <div
          data-testid="admin-form-modal"
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setShowForm(false)}
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
            className="w-full max-w-2xl bg-zinc-950 border border-zinc-800 p-8 my-8"
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-green-400 mb-1">
                  {editing ? "Edit" : "Create"}
                </p>
                <h3 className="font-display text-3xl uppercase tracking-tighter">
                  {editing ? editing.name : "New Product"}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                data-testid="admin-form-close"
                className="text-zinc-500 hover:text-white text-2xl"
                aria-label="close"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Name" span="md:col-span-2">
                <input
                  data-testid="form-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="admin-input"
                />
              </Field>

              <Field label="Description" span="md:col-span-2">
                <textarea
                  data-testid="form-description"
                  rows={3}
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  className="admin-input"
                />
              </Field>

              <Field label="Price (USD)">
                <input
                  data-testid="form-price"
                  type="number"
                  step="0.01"
                  required
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="admin-input"
                />
              </Field>

              <Field label="Stock">
                <input
                  data-testid="form-stock"
                  type="number"
                  value={form.stock}
                  onChange={(e) => setForm({ ...form, stock: e.target.value })}
                  className="admin-input"
                />
              </Field>

              <Field label="Category">
                <input
                  data-testid="form-category"
                  value={form.category}
                  onChange={(e) =>
                    setForm({ ...form, category: e.target.value })
                  }
                  className="admin-input"
                />
              </Field>

              <Field label="Sizes (comma separated)">
                <input
                  data-testid="form-sizes"
                  value={
                    Array.isArray(form.sizes) ? form.sizes.join(", ") : form.sizes
                  }
                  onChange={(e) =>
                    setForm({ ...form, sizes: e.target.value })
                  }
                  className="admin-input"
                />
              </Field>

              <Field label="Image URL" span="md:col-span-2">
                <input
                  data-testid="form-image-url"
                  required
                  value={form.image_url}
                  onChange={(e) =>
                    setForm({ ...form, image_url: e.target.value })
                  }
                  placeholder="https://..."
                  className="admin-input"
                />
              </Field>

              <label
                className="md:col-span-2 flex items-center gap-3 text-sm text-zinc-300 cursor-pointer select-none"
                data-testid="form-featured-label"
              >
                <input
                  data-testid="form-featured"
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) =>
                    setForm({ ...form, featured: e.target.checked })
                  }
                  className="w-4 h-4 accent-green-500"
                />
                <span className="uppercase tracking-widest text-xs">
                  Feature on landing page
                </span>
              </label>
            </div>

            <div className="mt-8 flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                data-testid="admin-form-cancel"
                className="border border-zinc-800 hover:border-white px-6 py-3 text-xs uppercase tracking-widest transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="admin-form-submit"
                className="bg-green-500 hover:bg-green-400 text-black font-bold px-6 py-3 text-xs uppercase tracking-widest transition-colors"
              >
                {editing ? "Save changes" : "Create product"}
              </button>
            </div>
          </form>
        </div>
      )}

      <style>{`
        .admin-input {
          width: 100%;
          background: #000;
          border: 1px solid #27272a;
          color: #fff;
          padding: 0.7rem 0.85rem;
          font-size: 0.9rem;
          outline: none;
          transition: border-color .15s ease;
        }
        .admin-input:focus { border-color: #4ade80; }
      `}</style>
    </div>
  );
}

function Field({ label, children, span = "" }) {
  return (
    <label className={`block ${span}`}>
      <span className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 block">
        {label}
      </span>
      {children}
    </label>
  );
}
